import React, { useState, useEffect, useRef } from 'react';
import './SlidingDrawer.css';
import { format, addWeeks, parseISO } from 'date-fns';
import { fetchWithAuth } from './utils';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface SlidingDrawerProps {
  isOpen: boolean;
  selectedRowData: any;
  onClose: () => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface WeekData {
  date: string;
  projectName: string;
  projectCode: string | null;
  weekNumber: string;
  manning_project_info_id: number | null;
  backgroundColor?: string; // Added for background color
}

interface Project {
  project_code: string;
  project_name: string;
  client_name: string;
}

interface CommentData {
  manning_comment_id: number;
  manning_info_id: number;
  comment: string;
  commented_by: string;
  commented_date: string;
}

const SlidingDrawer: React.FC<SlidingDrawerProps> = ({ isOpen, selectedRowData, onClose }) => {
const [openAccordion, setOpenAccordion] = useState<'info' | 'weekly' | 'comments' | null>('weekly');
const [weeklyProjectNames, setWeeklyProjectNames] = useState<WeekData[]>([]);
const [projects, setProjects] = useState<Project[]>([]);
const [comments, setComments] = useState<CommentData[]>([]);
const [newComment, setNewComment] = useState<string>('');
const [selectedColor, setSelectedColor] = useState<string>(''); // State for selected color
const [showSaveButton, setShowSaveButton] = useState(false);
const drawerRef = useRef<HTMLDivElement | null>(null);
const [showModal, setShowModal] = useState(false);
const [selectedUtilization, setSelectedUtilization] = useState<number>(0);
const [selectedProjects, setSelectedProjects] = useState<{ projectCode: string; projectName: string; utilization?: number; }[]>([]);
const [loading, setLoading] = useState<boolean>(false);
const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
const [colorToApply, setColorToApply] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setOpenAccordion('weekly');
      fetchProjects();
      fetchManningInfo(selectedRowData.manning_info_id);
      fetchComments(selectedRowData.manning_info_id);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const fetchProjects = async () => {
    try {
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_projects&company_code=DG&region_code=${selectedRowData.region_code}`
      const data = await fetchWithAuth(url);
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchManningInfo = async (manningId: string) => {
    try {
      setLoading(true); // Set loading to true when starting the fetch
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_manning_project_info&manning_id=${manningId}`;
      const data = await fetchWithAuth(url);
      initializeWeekData(data);
    } catch (error) {
      console.error('Error fetching Manning Info:', error);
    } finally {
      setLoading(false); // Set loading to false once the fetch is complete
    }
  };

  const fetchComments = async (manningId: string) => {
    try {
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_manning_comments&manning_id=${manningId}`
      const data = await fetchWithAuth(url);
      setComments(data || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const utilizationColorMapping = (utilization: number) => {
    if (utilization === 100) return '#ffffff'; // 100%
    if (utilization >= 75) return '#70ad47'; // 75%
    if (utilization >= 50) return '#ffe699'; // 50%
    if (utilization >= 25) return '#ed7d31'; // 25%
    if (utilization >= 0) return '#ffff00'; // <25%
    return '#8ea9db'; // NoPO or 0%
  };

  const initializeWeekData = (projects: any[]) => {
    if (!projects || projects.length === 0) {
      console.log("No project data available.");
      setWeeklyProjectNames([]);
      return;
    }
    const weeks: WeekData[] = [];
    const firstMonday = parseISO(projects[0]?.project_start_date);
    for (let i = 0; i < 12; i++) {
      const weekStartDate = addWeeks(firstMonday, i);
      const formattedDate = format(weekStartDate, 'MM/dd/yyyy');
      const project = projects[i] || {};
      const utilization = project.utilization_percentage || 0;
      weeks.push({
        date: formattedDate,
        projectName: project.project_name || 'No Project',
        projectCode: project.project_code || null,
        weekNumber: `Week ${i + 1}`,
        manning_project_info_id: project.manning_project_info_id || null,
        backgroundColor: utilizationColorMapping(utilization),
      });
    }
    setWeeklyProjectNames(weeks);
  };

  const handleProjectChange = (index: number, newProjectCode: string) => {
    const updatedWeeklyData = [...weeklyProjectNames];
    const selectedProject = projects.find((project) => project.project_code === newProjectCode);
    if (selectedProject) {
      for (let i = index; i < updatedWeeklyData.length; i++) {
        updatedWeeklyData[i] = {
          ...updatedWeeklyData[i],
          projectName: selectedProject.client_name,
          projectCode: selectedProject.project_code,
        };
      }
    }
  
    setWeeklyProjectNames(updatedWeeklyData);
    
    // If "Various" is selected, hide the Save button
    if (newProjectCode === 'Various') {
      setShowSaveButton(false);
    } else {
      setShowSaveButton(true);
    }
  };
  

  const handleSave = () => {
    weeklyProjectNames.forEach((week) => {
      if (week.manning_project_info_id && week.projectCode) {
        updateProjectNameAPI(week.manning_project_info_id, week.projectCode, week.projectName);
      }
    });
    setShowSaveButton(false);
  };

  const updateProjectNameAPI = async (manning_project_info_id: number, projectCode: string, projectName: string) => {
    try {
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=update_manning_project_info&manning_project_info_id=${manning_project_info_id}&projectCode=${projectCode}&projectName=${projectName}`;
  
      const response = await fetchWithAuth(url, {
        method: 'POST',
      });
  
      if (response) {
        console.log('Project name updated successfully:', response);
      } else {
        throw new Error('Error updating project');
      }
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  const handleColorSelect = (color: string) => {
    setColorToApply(color);
    setShowDatePicker(true);
  };
  

  const colorToUtilizationMap: Record<string, number | string> = {
    '#ffffff': 100,
    '#70ad47': 75,
    '#ffe699': 50,
    '#ed7d31': 25,
    '#ffff00': 0,
    '#8ea9db': 'NoPO',
  };
  
  const handleWeekBackgroundChange = async (index: number) => {
    if (!selectedColor) {
      console.warn('No color selected.');
      return;
    }
  
    const utilPercentage = colorToUtilizationMap[selectedColor];
    
    setWeeklyProjectNames((prev) => {
      const newWeeklyData = [...prev];
      const weekData = newWeeklyData[index];
  
      // Preserve existing project info, only update backgroundColor
      newWeeklyData[index] = {
        ...weekData,
        backgroundColor: selectedColor,
      };
  
      return newWeeklyData;
    });
  
    try {
      const week = weeklyProjectNames[index];
      if (!week.manning_project_info_id) return;
  
      const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=update_manning_project_utilization';
  
      const response = await fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify({
          manning_project_info_id: week.manning_project_info_id,
          util_percentage: utilPercentage,
        }),
      });
  
      if (!response) {
        throw new Error('Failed to update utilization');
      }
  
      console.log('Utilization updated successfully.');
    } catch (error) {
      console.error('Error updating utilization:', error);
    }
  };
        

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
  
    try {
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=add_manning_comments&manning_id=${selectedRowData.manning_info_id}`;
  
      const response = await fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify({
          comment: newComment,
          commented_by: 'Test User',
        }),
      });
  
      if (response) {
        fetchComments(selectedRowData.manning_info_id);
        setNewComment('');
        console.log('Comment added successfully:', response);
      } else {
        console.error('Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };
  
  const handleProjectSelection = (code: string) => {
    const selectedProject = projects.find((proj) => proj.project_code === code);
    if (selectedProject) {
      setSelectedProjects((prev) => [
        ...prev,
        { projectCode: selectedProject.project_code, projectName: selectedProject.client_name }
      ]);
    }
  };
   
  
  
  const handleUtilizationChange = (util: number) => {
    setSelectedUtilization(util);
  
    if (selectedProjects.length > 0) {
      setSelectedProjects((prev) => {
        const updatedProjects = [...prev];
        const lastIndex = updatedProjects.length - 1;
        updatedProjects[lastIndex] = {
          ...updatedProjects[lastIndex],
          utilization: util,
        };
        return updatedProjects;
      });
    }
  };
  
  
  
  const handleAddVariousProjects = async (manningProjectInfoId: number) => {
    try {
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=add_various_projects`;
      await fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify({ projects: selectedProjects, utilization: selectedUtilization }),
      });
      setShowModal(false);
      console.log('Various projects added successfully.');
    } catch (error) {
      console.error('Error adding various projects:', error);
    }
  };


  const handleSaveSelectedProjects = async () => {
    try {
      console.log("Weekly Project Names:", JSON.stringify(weeklyProjectNames, null, 2));

      const variousWeeks = weeklyProjectNames
      .filter((week) => week.projectName === 'Various' && week.manning_project_info_id !== null)
      .map((week) => week.manning_project_info_id);
    
    console.log("Various Weeks (Filtered):", variousWeeks);
    
      if (variousWeeks.length === 0) {
        console.warn('No weeks selected as Various');
        return;
      }
  
      const payload = {
        manning_project_info_ids: variousWeeks,
        manning_info_id: selectedRowData.manning_info_id,
        projects: selectedProjects.map((proj) => ({
          projectCode: proj.projectCode,
          projectName: proj.projectName,
          utilization: proj.utilization,
        })),
      };
  
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=add_various_project_info`;
      const response = await fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
  
      if (response) {
        console.log('Selected projects saved successfully.');
        setShowModal(false);
        setSelectedProjects([]);
        setSelectedUtilization(0);
      } else {
        throw new Error('Error saving selected projects');
      }
    } catch (error) {
      console.error('Error saving selected projects:', error);
    }
  };
  
  const handleRemoveProject = (indexToRemove: number) => {
    setSelectedProjects((prev) => prev.filter((_, index) => index !== indexToRemove));
  };
  
  const projectOptions = projects.map((project) => ({
    value: project.project_code,
    label: `${project.project_code} - ${project.client_name} - ${project.project_name}`,
  }));

  const applyColorUntilDate = async (selectedDate: Date) => {
    const formattedSelected = format(selectedDate, 'MM/dd/yyyy');
  
    const updatedWeeks = [...weeklyProjectNames];
    for (let i = 0; i < updatedWeeks.length; i++) {
      const weekDate = updatedWeeks[i].date;
      if (new Date(weekDate) <= new Date(formattedSelected)) {
        const color = colorToApply;
        updatedWeeks[i] = {
          ...updatedWeeks[i],
          backgroundColor: color,
        };
  
        if (updatedWeeks[i].manning_project_info_id) {
          try {
            const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=update_manning_project_utilization';
            await fetchWithAuth(url, {
              method: 'POST',
              body: JSON.stringify({
                manning_project_info_id: updatedWeeks[i].manning_project_info_id,
                util_percentage: colorToUtilizationMap[colorToApply],
              }),
            });
          } catch (error) {
            console.error('Error updating utilization:', error);
          }
        }
      }
    }
  
    setWeeklyProjectNames(updatedWeeks);
  };
  
  return (
    <div ref={drawerRef} className={`drawer ${isOpen ? 'open' : ''}`}>
      {/* Manning/Project Info Accordion */}
      <div className="accordion">
        <button className="accordion-header" onClick={() => setOpenAccordion(openAccordion === 'info' ? null : 'info')}>
          Manning/Project Info - {selectedRowData.name}
        </button>
        {openAccordion === 'info' && (
          <div className="accordion-content">
            <div className="details-list">
              <div className="detail-item"><span>Name:</span> {selectedRowData.name}</div>
              <div className="detail-item"><span>Email:</span> {selectedRowData.email}</div>
              <div className="detail-item"><span>Title:</span> {selectedRowData.title}</div>
              <div className="detail-item"><span>Type:</span> {selectedRowData.type}</div>
              <div className="detail-item"><span>Primary Practice:</span> {selectedRowData.primary_practice}</div>
            </div>
          </div>
        )}
      </div>
      <div className="accordion">
        <button className="accordion-header" onClick={() => setOpenAccordion(openAccordion === 'weekly' ? null : 'weekly')}>
          Project Weekly View - {selectedRowData.name}
        </button>
      {openAccordion === 'weekly' && (
        <div className="accordion-content">
          {loading ? (
            <div className="loading-message">Loading Projects...</div> // Loading message for Projects
          ) : (
            <>
              {/* Color Picker Section */}

              <div className="color-picker">
                {[
                  { color: '#ffffff', label: '100%' },
                  { color: '#70ad47', label: '75%' },
                  { color: '#ffe699', label: '50%' },
                  { color: '#ed7d31', label: '25%' },
                  { color: '#ffff00', label: '0%' },
                  { color: '#8ea9db', label: 'NoPO' },
                ].map(({ color, label }) => (
                  <button
                    key={color}
                    className="color-option"
                    style={{
                      backgroundColor: color,
                      position: 'relative',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      margin: '5px',
                    }}
                    onClick={() => handleColorSelect(color)}
                  >
                    <span
                      style={{
                        fontSize: '10px',
                        color: color === '#ffffff' ? '#000' : '#fff',
                        fontWeight: 'bold',
                        pointerEvents: 'none',
                      }}
                    >
                      {label}
                    </span>
                  </button>
                ))}
              </div>
              {showSaveButton && (
                <div className="save-button-container">
                  <button className="save-button" onClick={handleSave}>Save</button>
                </div>
              )}
              {/* Week Table */}
              <div className="week-table">
                {weeklyProjectNames.map((week, index) => (
                  <div
                    key={index}
                    className="week-item"
                    style={{ backgroundColor: week.backgroundColor || '#fff' }}
                    onClick={() => handleWeekBackgroundChange(index)}
                  >
                    <div>{week.date} - {week.weekNumber}</div>

                    <div className="dropdown-wrapper" style={{ minWidth: '300px' }}>
                      <Select
                        options={projectOptions}
                        value={projectOptions.find((option) => option.value === week.projectCode) || null}
                        onChange={(selectedOption) => {
                          if (!selectedOption) return;
                          handleProjectChange(index, selectedOption.value);
                          if (selectedOption.value === 'Various') {
                            setShowModal(true);
                          }
                        }}
                        isSearchable
                        placeholder="Select a project"
                      />
                    </div>

                  </div>
                ))}
              </div>
            </>
          )}

          {showSaveButton && (
            <div className="save-button-container">
              <button className="save-button" onClick={handleSave}>Save</button>
            </div>
          )}
        </div>
      )}
      </div>

      {/* Manning Meeting Comments Accordion */}
      <div className="accordion">
        <button
          className="accordion-header"
          onClick={() => setOpenAccordion(openAccordion === 'comments' ? null : 'comments')}
        >
          Manning Meeting Comments - {selectedRowData.name}
        </button>
        {openAccordion === 'comments' && (
          <div className="accordion-content">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              rows={3}
              className="comments-textarea"
            />
            <button className="add-comment-button" onClick={handleAddComment}>
              Add Comment
            </button>
            <div className="comments-section">
              {comments.length === 0 ? (
                <p>No comments yet.</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.manning_comment_id} className="comment-row">
                    <span className="comment-text" title={`By: ${comment.commented_by} - ${comment.commented_date}`}>
                      {comment.comment}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      {/* Modal Starts */}
      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Select Project</h3>
            <Select
              className="wide-select"
              options={projects.map((project) => ({
                value: project.project_code,
                label: `${project.project_code} - ${project.client_name} - ${project.project_name}`,
              }))}
              value={
                selectedProjects.length > 0
                  ? {
                      value: selectedProjects[selectedProjects.length - 1].projectCode,
                      label: `${selectedProjects[selectedProjects.length - 1].projectCode} - ${
                        projects.find((p) => p.project_code === selectedProjects[selectedProjects.length - 1].projectCode)?.client_name
                      } - ${
                        projects.find((p) => p.project_code === selectedProjects[selectedProjects.length - 1].projectCode)?.project_name
                      }`,
                    }
                  : null
              }
              onChange={(selectedOption) => {
                if (selectedOption) {
                  handleProjectSelection(selectedOption.value);
                }
              }}
              isSearchable
              placeholder="Select a project"
            />
            <h3>Select Utilization</h3>
            <select
              value={selectedUtilization}
              onChange={(e) => handleUtilizationChange(parseInt(e.target.value))}
              style={{
                width: '100%',
                padding: '5px',
                marginBottom: '10px',
              }}
            >
              <option value="" disabled>Select utilization</option>
              {[0, 25, 50, 75, 100].map((util) => (
                <option key={util} value={util}>
                  {util}%
                </option>
              ))}
            </select>
            {selectedProjects.map((item, index) => (
            <div key={index} className="selected-project-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>
                {item.projectName} ({item.projectCode}) - {item.utilization}%
              </span>
              <button
                onClick={() => handleRemoveProject(index)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#d9534f',
                  cursor: 'pointer',
                  marginLeft: '10px',
                  fontSize: '16px',
                }}
                title="Remove project"
              >
                ❌
              </button>
            </div>
          ))}


            <div className="save-button-container">
              <button onClick={handleSaveSelectedProjects} className="save-button">Save</button>
              <button onClick={() => setShowModal(false)} className="save-button">Close</button>
            </div>
            
          </div>
        </div>
      )}
      {/* Modal ends */}

      {showDatePicker && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Select a week to set Utilization</h2>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <DatePicker
                selected={null}
                onChange={(date: Date | null) => {
                  if (date) {
                    applyColorUntilDate(date);
                    setShowDatePicker(false);
                  }
                }}
                filterDate={(date: Date) => date.getDay() === 1}
                inline
              />
              <button className="save-button" onClick={() => setShowDatePicker(false)} style={{ marginTop: '10px' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};


export default SlidingDrawer;
